import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, Pencil, Trash2, Video, PlayCircle } from 'lucide-react';
import { get, post, patch, del } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Modal from './Modal';

const EMPTY = {
  title: '',
  speaker: '',
  series: '',
  sermon_date: '',
  media_url: '',
  description: '',
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Convert a stored ISO datetime into a value for <input type="date">.
function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export default function Content() {
  const { isPastor, isViewer } = useAuth();
  const [sermons, setSermons] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState(null); // sermon object or {} for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      const data = await get('/api/v1/content/sermons', { params });
      setSermons(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => {
    setForm(EMPTY);
    setFormError('');
    setEditing({});
  };

  const openEdit = (s) => {
    setForm({
      title: s.title || '',
      speaker: s.speaker || '',
      series: s.series || '',
      sermon_date: toDateInput(s.sermon_date),
      media_url: s.media_url || '',
      description: s.description || '',
    });
    setFormError('');
    setEditing(s);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    // Only send non-empty fields.
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '' && v !== null) payload[k] = v;
    });
    try {
      if (editing.id) {
        await patch(`/api/v1/content/sermons/${editing.id}`, payload);
      } else {
        await post('/api/v1/content/sermons', payload);
      }
      setEditing(null);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sermon "${s.title}"?`)) return;
    try {
      await del(`/api/v1/content/sermons/${s.id}`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            Sermons
          </h2>
          {isViewer && (
            <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
              Read-only demo
            </span>
          )}
        </div>
        {!isViewer && (
          <button
            onClick={openNew}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center gap-1"
          >
            <Plus size={16} /> Add sermon
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, speaker or series…"
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center text-slate-400">
          Loading…
        </div>
      ) : sermons.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-400 flex flex-col items-center gap-2">
          <Video size={28} />
          No sermons found.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sermons.map((s) => (
            <div
              key={s.id}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-semibold text-slate-800 dark:text-slate-100 leading-snug">
                  {s.title}
                </h3>
                {!isViewer && (
                  <div className="flex gap-2 text-slate-400 shrink-0">
                    <button
                      onClick={() => openEdit(s)}
                      className="hover:text-brand-600"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    {isPastor && (
                      <button
                        onClick={() => remove(s)}
                        className="hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {s.speaker && (
                <p className="text-sm text-slate-500 dark:text-slate-400">{s.speaker}</p>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-2">
                {s.series && (
                  <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                    {s.series}
                  </span>
                )}
                {formatDate(s.sermon_date) && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {formatDate(s.sermon_date)}
                  </span>
                )}
              </div>

              {s.description && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 line-clamp-3">
                  {s.description}
                </p>
              )}

              {s.media_url && (
                <a
                  href={s.media_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700"
                >
                  <PlayCircle size={16} /> Watch / Listen
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal
          title={editing.id ? 'Edit sermon' : 'Add sermon'}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form onSubmit={save} className="space-y-3">
            {formError && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                {formError}
              </div>
            )}
            <Field label="Title">
              <input
                required
                className={inputCls}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Speaker">
                <input
                  className={inputCls}
                  value={form.speaker}
                  onChange={(e) => setForm({ ...form, speaker: e.target.value })}
                />
              </Field>
              <Field label="Series">
                <input
                  className={inputCls}
                  value={form.series}
                  onChange={(e) => setForm({ ...form, series: e.target.value })}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date">
                <input
                  type="date"
                  className={inputCls}
                  value={form.sermon_date}
                  onChange={(e) => setForm({ ...form, sermon_date: e.target.value })}
                />
              </Field>
              <Field label="Media URL">
                <input
                  type="url"
                  placeholder="https://…"
                  className={inputCls}
                  value={form.media_url}
                  onChange={(e) => setForm({ ...form, media_url: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Description">
              <textarea
                rows={4}
                className={inputCls}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
          </form>
        </Modal>
      )}
    </div>
  );
}
